/**
 * ProLayout.tsx
 * 
 * Three-zone responsive layout for the Pro editor.
 * Composes: Toolbar + Sidebar + Preview + Timeline + Inspector
 */

import React, { useMemo, useCallback, useEffect, useState, useRef, DragEvent } from 'react';
import type { useEditorCore } from '../../hooks/useEditorCore';
import type { Clip } from '../../types';
import { ProSidebar } from './ProSidebar';
import { ProInspector } from './ProInspector';
import { ProTimeline } from './ProTimeline';
import { ProTopBar } from '../../PRO/components/ProTopBar';
import { ProPreviewCanvas } from '../../PRO/components/ProPreviewCanvas';
import { ProLoadingOverlay } from '../../PRO/components/ProLoadingOverlay';

type EditorCore = ReturnType<typeof useEditorCore>;

interface ProLayoutProps {
    editor: EditorCore;
}

export const ProLayout: React.FC<ProLayoutProps> = ({ editor }) => {
    const {
        user,
        activeTab, setActiveTab,
        isInitialDropzoneVisible,
        projectLoadingState,
        scaleFactor,

        // Clip management
        allClips, setAllClips,
        tracks, setTracks,
        selectedClipId, setSelectedClipId,
        selectedClip,
        isClipContextMenuOpen, setIsClipContextMenuOpen,
        handleClipSelect,
        history, historyIndex, undo, redo,
        navigationStack,
        currentScope,
        updateClips,

        // Chat
        messages, setMessages,
        editingSuggestions,
        handleSendMessage,
        handleEditChoice,

        // Project
        currentProjectId,
        savedProjects,
        folders, setFolders,
        rootProjects, setRootProjects,
        sidebarOpen, setSidebarOpen,
        loadProject,

        // Export
        isExporting,
        exportStatus,
        exportProgress,
        exportDetails,
        exportedVideos,
        exportAspectRatio, setExportAspectRatio,
        scaleMode,
        vfxSegmentPreviews,
        serverExportStatus,
        activeServerExports,
        chunks,
        isMerging,
        handleRetryChunk,
        handleStartHybridExport,
        hasTimelineOverlays,
        totalTimelineDuration,
        isTimelineEligibleForFastExport,
        handleStartExport,
        handleCancelExport,
        handleStartServerExport,
        handleCancelServerExport,
        handleDeleteExport,
        exportStatusByProject,

        // Subtitles
        subtitleStyle, setSubtitleStyle,
        activeSubtitleLanguage, setActiveSubtitleLanguage,
        handleTranslateSubtitles,
        translationsInProgress,

        // Montage versions
        montageVersions,
        currentVersionId,
        handleSelectVersion,
        ensureBaseVersion,

        // Voiceover
        generatedVoices, setGeneratedVoices,
        voiceoverText, setVoiceoverText,

        // Handlers
        handleClipVolumeChange,
        handleSelectAudioSource,
        handleManualSave,
        handleInitialFilesDrop,
        handleOpenExportMenu,
        handleSeekRequest,
        handleSubtitleClick,
        handleCloseClipContextMenu,
        handleAddLibraryItem,
        handleSelectSidebarProject,
        handleDeleteProject,
        handleDuplicateProject,
        handleRenameProject,
        handleShareProject,
        handleSaveMaster,
        navigateIntoClip,
        navigateUp,

        // Refs
        timelinePlayerRef,

        // Misc
        selectedSubtitleId,
        INITIAL_TRACKS,
        useGpt4oTranscribe, setUseGpt4oTranscribe,
    } = editor;

    // Current project name
    const currentProjectName = useMemo(() => {
        const p = savedProjects.find(p => p.id === currentProjectId);
        return p?.name || 'Projet sans titre';
    }, [savedProjects, currentProjectId]);

    // Inspector visibility
    const [inspectorOpen, setInspectorOpen] = useState(true);

    // Auto-show inspector when clip selected
    useEffect(() => {
        if (selectedClip) {
            setInspectorOpen(true);
        }
    }, [selectedClip]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const isMeta = e.metaKey || e.ctrlKey;

            // Don't intercept if user is typing
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.contentEditable === 'true') return;

            if (isMeta && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                undo();
            }
            if (isMeta && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault();
                redo();
            }
            if (isMeta && e.key === 's') {
                e.preventDefault();
                handleManualSave();
            }
            if (isMeta && e.key === 'e') {
                e.preventDefault();
                handleOpenExportMenu();
            }
            if (e.key === 'Escape') {
                setSelectedClipId(null);
                setIsClipContextMenuOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo, handleManualSave, handleOpenExportMenu]);

    // Wrapper for clip selection to handle UI side effects
    const onClipSelect = useCallback((clip: Clip | null) => {
        handleClipSelect(clip);
        if (clip) {
            setActiveTab('assistant');
        }
    }, [handleClipSelect, setActiveTab]);

    // --- Drag & Drop on main area ---
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleInitialFilesDrop(files);
        }
    }, [handleInitialFilesDrop]);

    // --- Navigation breadcrumb ---
    const isNested = navigationStack.length > 0;

    return (
        <div className="pro-editor">
            <ProTopBar
                sidebarOpen={sidebarOpen}
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                isNested={isNested}
                onNavigateUp={navigateUp}
                currentProjectName={currentProjectName}
                historyIndex={historyIndex}
                historyLength={history.length}
                onUndo={undo}
                onRedo={redo}
                onManualSave={handleManualSave}
                onOpenExport={handleOpenExportMenu}
                inspectorOpen={inspectorOpen}
                onToggleInspector={() => setInspectorOpen(!inspectorOpen)}
            />

            {/* ================================================================
          SIDEBAR
          ================================================================ */}
            <ProSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                projects={rootProjects}
                folders={folders}
                savedProjects={savedProjects}
                currentProjectId={currentProjectId}
                onSelectProject={handleSelectSidebarProject}
                onDeleteProject={handleDeleteProject}
                onDuplicateProject={handleDuplicateProject}
                onRenameProject={handleRenameProject}
                onShareProject={handleShareProject}
                setFolders={setFolders}
                setRootProjects={setRootProjects}
                user={user}
                exportStatusByProject={exportStatusByProject}
            />

            <ProPreviewCanvas
                isInitialDropzoneVisible={isInitialDropzoneVisible}
                isDragOver={isDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onInitialFilesDrop={handleInitialFilesDrop}
                timelinePlayerRef={timelinePlayerRef}
                currentScope={currentScope}
                selectedClipId={selectedClipId}
                onClipSelect={onClipSelect}
                subtitleStyle={subtitleStyle}
                activeSubtitleLanguage={activeSubtitleLanguage}
                onActiveSubtitleLanguageChange={setActiveSubtitleLanguage}
                onTranslateSubtitles={handleTranslateSubtitles}
                onSubtitleClick={handleSubtitleClick}
                onNavigateInto={navigateIntoClip}
            />

            {/* ================================================================
          INSPECTOR
          ================================================================ */}
            <ProInspector
                isOpen={inspectorOpen}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedClip={selectedClip}
                allClips={allClips}
                setAllClips={setAllClips}
                tracks={tracks}
                messages={messages}
                onSendMessage={handleSendMessage}
                onChoiceSelect={handleEditChoice}
                editingSuggestions={editingSuggestions}

                // Export props
                isExporting={isExporting}
                exportStatus={exportStatus}
                exportProgress={exportProgress}
                exportDetails={exportDetails}
                exportedVideos={exportedVideos}
                onStartExport={handleStartExport}
                onCancelExport={handleCancelExport}
                exportAspectRatio={exportAspectRatio}
                onExportAspectRatioChange={setExportAspectRatio}
                isTimelineEligibleForFastExport={isTimelineEligibleForFastExport}
                vfxSegmentPreviews={vfxSegmentPreviews}
                onStartServerExport={handleStartServerExport}
                serverExportStatus={serverExportStatus}
                onCancelServerExport={handleCancelServerExport}
                onDeleteExport={handleDeleteExport}
                projectName={currentProjectName}
                chunks={chunks}
                isMerging={isMerging}
                onRetryChunk={handleRetryChunk}
                onStartHybridExport={handleStartHybridExport}
                hasTimelineOverlays={hasTimelineOverlays}
                totalTimelineDuration={totalTimelineDuration}
                activeServerExports={activeServerExports}

                // Subtitle props
                subtitleStyle={subtitleStyle}
                setSubtitleStyle={setSubtitleStyle}
                onSeekRequest={handleSeekRequest}
                activeSubtitleLanguage={activeSubtitleLanguage}
                onActiveSubtitleLanguageChange={setActiveSubtitleLanguage}
                onTranslateSubtitles={handleTranslateSubtitles}
                translationsInProgress={translationsInProgress}
                selectedClipId={selectedClipId}
                setSelectedClipId={setSelectedClipId}
                selectedSubtitleId={selectedSubtitleId}

                // Actions
                onDeleteClip={editor.handleDeleteClip}

                // Volume / enhancement handlers
                onClipVolumeChange={(v) => selectedClipId && handleClipVolumeChange(selectedClipId, v)}
                onClipVolumeCommit={(v) => selectedClipId && handleClipVolumeChange(selectedClipId, v, true)}
                onSelectAudioSource={handleSelectAudioSource}

                // Library / voiceover
                onAddLibraryItem={handleAddLibraryItem}
                generatedVoices={generatedVoices}
                setGeneratedVoices={setGeneratedVoices}
                voiceoverText={voiceoverText}
                setVoiceoverText={setVoiceoverText}

                // Master
                currentScope={currentScope}
                isRestrictedMode={isNested}
                onSaveMaster={handleSaveMaster}

                user={user}
                scaleFactor={scaleFactor}
                setScaleFactor={editor.setScaleFactor}
            />

            {/* ================================================================
          TIMELINE
          ================================================================ */}
            <ProTimeline
                clips={currentScope.clips}
                tracks={currentScope.tracks}
                allClips={allClips}
                updateClips={updateClips}
                setTracks={setTracks}
                selectedClipId={selectedClipId}
                onClipSelect={onClipSelect}
                timelinePlayerRef={timelinePlayerRef}
                montageVersions={montageVersions}
                currentVersionId={currentVersionId}
                onSelectVersion={handleSelectVersion}
                onNavigateInto={navigateIntoClip}
            />

            {projectLoadingState.isLoading && (
                <ProLoadingOverlay thumbnailUrl={projectLoadingState.thumbnailUrl} />
            )}
        </div>
    );
};
